import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Instagram, ArrowRight, Loader2, CheckCircle, 
  AlertCircle, Sparkles, Download, Eye, X,
  RefreshCw, Clock, ChevronLeft, ChevronRight
} from 'lucide-react';
import { freeGenerationService } from '../../services/FreeGenerationService';

/**
 * Estados possíveis da seção:
 * - input: Mostra input para digitar @
 * - loading: Analisando perfil e criando batch
 * - processing: Batch criado, fazendo polling
 * - completed: Conteúdo gerado, mostrando resultado
 * - error: Erro na geração
 * - limit_reached: Já usou a geração gratuita
 */

const FreeGenerationSection = () => {
  const navigate = useNavigate();
  
  // Estados
  const [state, setState] = useState('input');
  const [username, setUsername] = useState('');
  const [batchId, setBatchId] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('Analisando seu perfil...');
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Estado do carrossel
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Mensagens de loading rotativas
  const loadingMessages = [
    'Analisando seu perfil...',
    'Identificando as cores da sua marca...',
    'Criando conteúdo personalizado...',
    'Quase lá, finalizando...',
    'Aplicando os últimos ajustes...'
  ];
  
  // Verificar se já usou ao carregar
  useEffect(() => {
    const checkUsage = async () => {
      const savedBatchId = freeGenerationService.getSavedBatchId();
      
      if (savedBatchId) {
        try {
          const result = await freeGenerationService.getBatchStatus(savedBatchId);
          
          if (result.success) {
            setBatchId(savedBatchId);
            setBatchData(result.data);
            
            if (result.data.status === 'completed') {
              setState('completed');
            } else if (result.data.status === 'failed') {
              setState('limit_reached');
            } else {
              setState('processing');
            }
            return;
          }
        } catch (e) {
          if (freeGenerationService.hasUsedFreeGeneration()) {
            setState('limit_reached');
            return;
          }
        }
      }
      
      if (freeGenerationService.hasUsedFreeGeneration()) {
        setState('limit_reached');
        return;
      }
    };
    
    checkUsage();
  }, []);
  
  // Polling do batch
  useEffect(() => {
    if (state !== 'processing' || !batchId) return;
    
    let isMounted = true;
    let messageIndex = 0;
    
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingMessage(loadingMessages[messageIndex]);
    }, 3000);
    
    const pollStatus = async () => {
      try {
        const result = await freeGenerationService.getBatchStatus(batchId);
        
        if (!isMounted) return;
        
        if (result.success) {
          setBatchData(result.data);
          
          if (result.data.status === 'completed') {
            setState('completed');
            clearInterval(messageInterval);
          } else if (result.data.status === 'failed') {
            setState('error');
            setError('Não foi possível gerar o conteúdo. Tente novamente mais tarde.');
            clearInterval(messageInterval);
          }
        }
      } catch (e) {
        console.error('Erro no polling:', e);
      }
    };
    
    pollStatus();
    const pollInterval = setInterval(pollStatus, 3000);
    
    return () => {
      isMounted = false;
      clearInterval(pollInterval);
      clearInterval(messageInterval);
    };
  }, [state, batchId]);
  
  // Submeter formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const cleanUsername = username.replace('@', '').trim();
    
    if (!cleanUsername) return;
    
    setState('loading');
    setLoadingMessage('Analisando seu perfil...');
    setError(null);
    
    try {
      const result = await freeGenerationService.createFreeGeneration(cleanUsername);
      
      if (result.success && result.data?.batch_id) {
        setBatchId(result.data.batch_id);
        setBatchData({ status: 'pending', progress: { total: 1, completed: 0 } });
        setState('processing');
      } else {
        throw new Error('Resposta inválida do servidor');
      }
      
    } catch (err) {
      console.error('Erro na geração:', err);
      
      if (err.code === 'limit_exceeded' || err.status === 403) {
        setState('limit_reached');
      } else {
        setState('error');
        setError(err.message || 'Não foi possível analisar o perfil. Verifique se o @ está correto.');
      }
    }
  };
  
  // Tentar novamente
  const handleRetry = () => {
    setState('input');
    setUsername('');
    setError(null);
    setBatchId(null);
    setBatchData(null);
    setCurrentSlide(0);
  };
  
  // Abrir preview
  const handlePreview = (url) => {
    setPreviewUrl(url);
  };
  
  // Fechar preview
  const closePreview = () => {
    setPreviewUrl(null);
  };
  
  // Download
  const handleDownload = (url, index = 0) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `hellosocial_post_${index + 1}_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Download de todos
  const handleDownloadAll = () => {
    const contents = getCompletedContents();
    contents.forEach((content, index) => {
      setTimeout(() => {
        handleDownload(content.result_url, index);
      }, index * 500);
    });
  };
  
  // Obter todos os conteúdos completados
  const getCompletedContents = () => {
    if (!batchData?.contents) return [];
    return batchData.contents.filter(c => c.status === 'completed' && c.result_url);
  };
  
  // Navegação do carrossel
  const nextSlide = () => {
    const contents = getCompletedContents();
    setCurrentSlide((prev) => (prev + 1) % contents.length);
  };
  
  const prevSlide = () => {
    const contents = getCompletedContents();
    setCurrentSlide((prev) => (prev - 1 + contents.length) % contents.length);
  };
  
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };
  
  // ============================================
  // RENDER: Estado INPUT (Formulário)
  // ============================================
  // Replace the input state return block:

if (state === 'input') {
    return (
      <div className="w-full max-w-xl mx-auto px-4">
        <form onSubmit={handleSubmit}>
          <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200 p-1.5 sm:p-2 flex items-center gap-1.5 sm:gap-2 overflow-hidden">
            <div className="flex items-center gap-2 pl-2 sm:pl-3 flex-shrink-0">
              <Instagram className="w-5 h-5 text-pink-500" />
            </div>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="@seuinstagram"
              className="flex-1 min-w-0 px-1 sm:px-2 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none rounded-xl bg-transparent"
            />
            <button
              type="submit"
              disabled={!username.trim()}
              className="px-3 sm:px-6 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-500 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 sm:gap-2 shadow-md hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5 whitespace-nowrap flex-shrink-0 text-sm sm:text-base"
            >
              <span className="hidden sm:inline">Gerar grátis</span>
              <span className="sm:hidden">Gerar</span>
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </form>
        
      </div>
    );
  }
  
  // ============================================
  // RENDER: Estado LOADING
  // ============================================
  if (state === 'loading') {
    return (
      <div className="w-full max-w-xl mx-auto">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {loadingMessage}
          </h3>
          <p className="text-gray-500">
            Isso levará apenas alguns segundos...
          </p>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Estado PROCESSING
  // ============================================
  if (state === 'processing') {
    const progress = batchData?.progress || { total: 1, completed: 0 };
    const percentage = Math.round((progress.completed / progress.total) * 100);
    
    return (
      <div className="w-full max-w-xl mx-auto">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center animate-pulse">
            <Sparkles className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Criando seus conteúdos...
          </h3>
          <p className="text-gray-500 mb-6">
            {loadingMessage}
          </p>
          
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
            <div 
              className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500"
              style={{ width: `${Math.max(percentage, 10)}%` }}
            />
          </div>
          <p className="text-sm text-gray-400">
            {progress.completed} de {progress.total} • {percentage}%
          </p>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Estado COMPLETED
  // ============================================
  if (state === 'completed') {
    const contents = getCompletedContents();
    const isSingleContent = contents.length === 1;
    const isMultipleContents = contents.length > 1;
    
    return (
      <div className="w-full max-w-3xl mx-auto">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Header de sucesso */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white text-center">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <span className="font-semibold">
                {contents.length} {contents.length === 1 ? 'conteúdo criado' : 'conteúdos criados'} com sucesso!
              </span>
            </div>
          </div>
          
          {/* Conteúdo */}
          <div className="p-6">
            {/* === LAYOUT ÚNICO (1 conteúdo) === */}
            {isSingleContent && contents[0]?.result_url && (
              <>
                <div 
                  className="relative aspect-square max-w-md mx-auto rounded-xl overflow-hidden shadow-lg mb-6 group cursor-pointer"
                  onClick={() => handlePreview(contents[0].result_url)}
                >
                  <img 
                    src={contents[0].result_url} 
                    alt="Conteúdo gerado"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Eye className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <div className="flex gap-3 justify-center mb-6">
                  <button
                    onClick={() => handleDownload(contents[0].result_url, 0)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Baixar imagem
                  </button>
                  <button
                    onClick={() => handlePreview(contents[0].result_url)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Ampliar
                  </button>
                </div>
              </>
            )}
            
            {/* === CARROSSEL (múltiplos conteúdos) === */}
            {isMultipleContents && (
              <>
                {/* Container do Carrossel */}
                <div className="relative mb-6">
                  {/* Imagem principal */}
                  <div className="relative aspect-square max-w-lg mx-auto rounded-xl overflow-hidden shadow-lg">
                    <div 
                      className="flex transition-transform duration-500 ease-out h-full"
                      style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                    >
                      {contents.map((content, index) => (
                        <div 
                          key={content.id} 
                          className="w-full h-full flex-shrink-0 cursor-pointer"
                          onClick={() => handlePreview(content.result_url)}
                        >
                          <img 
                            src={content.result_url} 
                            alt={`Conteúdo ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                    
                    {/* Overlay com Eye no hover */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                      <Eye className="w-10 h-10 text-white" />
                    </div>
                    
                    {/* Badge do slide atual */}
                    <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-sm font-semibold">
                      {currentSlide + 1} / {contents.length}
                    </div>
                  </div>
                  
                  {/* Botões de navegação */}
                  <button
                    onClick={prevSlide}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                  >
                    <ChevronLeft className="w-6 h-6 text-gray-700" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
                  >
                    <ChevronRight className="w-6 h-6 text-gray-700" />
                  </button>
                </div>
                
                {/* Indicadores (dots) */}
                <div className="flex justify-center gap-2 mb-6">
                  {contents.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        index === currentSlide 
                          ? 'bg-amber-500 w-8' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
                
                {/* Thumbnails */}
                <div className="flex justify-center gap-3 mb-6">
                  {contents.map((content, index) => (
                    <button
                      key={content.id}
                      onClick={() => goToSlide(index)}
                      className={`relative w-16 h-16 rounded-lg overflow-hidden transition-all ${
                        index === currentSlide 
                          ? 'ring-2 ring-amber-500 ring-offset-2 scale-105' 
                          : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img 
                        src={content.result_url} 
                        alt={`Thumb ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
                
                {/* Botões de ação */}
                <div className="flex flex-wrap gap-3 justify-center mb-6">
                  <button
                    onClick={() => handleDownload(contents[currentSlide].result_url, currentSlide)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Baixar atual
                  </button>
                  <button
                    onClick={handleDownloadAll}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-500 hover:to-orange-600 transition-all"
                  >
                    <Download className="w-4 h-4" />
                    Baixar todos ({contents.length})
                  </button>
                  <button
                    onClick={() => handlePreview(contents[currentSlide].result_url)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Ampliar
                  </button>
                </div>
              </>
            )}
            
            {/* Caso não tenha conteúdos */}
            {contents.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">Conteúdos processados.</p>
              </div>
            )}
          </div>
          
          {/* CTA para cadastro */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 border-t border-amber-100">
            <div className="text-center">
              <h4 className="text-lg font-bold text-gray-900 mb-2">
                Gostou? Crie seus posts!
              </h4>
              <p className="text-gray-600 mb-4 text-sm">
                Cadastre-se grátis e tenha acesso a mais modelos de IA, publicação automática e muito mais.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold rounded-xl hover:from-amber-500 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl hover:shadow-amber-500/25"
              >
                Criar minha conta grátis
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
        
        {/* Modal de Preview */}
        {previewUrl && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={closePreview}
          >
            <button
              onClick={closePreview}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={previewUrl} 
              alt="Preview"
              className="max-w-full max-h-[90vh] rounded-xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    );
  }
  
  // ============================================
  // RENDER: Estado ERROR
  // ============================================
  if (state === 'error') {
    return (
      <div className="w-full max-w-xl mx-auto">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-red-200 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-2xl flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Ops! Algo deu errado
          </h3>
          <p className="text-gray-600 mb-6">
            {error || 'Não foi possível gerar o conteúdo. Tente novamente.'}
          </p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER: Estado LIMIT_REACHED
  // ============================================
  if (state === 'limit_reached') {
    return (
      <div className="w-full max-w-xl mx-auto">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-2xl flex items-center justify-center">
            <Clock className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Você já usou seu teste grátis
          </h3>
          <p className="text-gray-600 mb-6">
            Crie uma conta para gerar conteúdos ilimitados!
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold rounded-xl hover:from-amber-500 hover:to-orange-600 transition-all shadow-lg"
            >
              Criar conta grátis
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
            >
              Já tenho conta
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

export default FreeGenerationSection;